import { Component, Input, OnInit, inject } from '@angular/core';
import { AirportCatalogService } from 'src/app/core/services/airport-catalog/airport-catalog.service';
import { FlightSegmentSch, OriginDestinationOption } from '../models/OTAAirScheduleResponse';
import { CityLookup } from '../flight-ticket-frame/flight-ticket-frame.component';
import { HeaderSchedule, ItinerarySummary, formatDuration, headerSchedule, summarizeCardItinerary, toLegs } from '../flight-ticket-frame/flight-ticket.util';
import { FlightDetailsPanelService } from '../flight-details-dialog/flight-details-dialog.component';

/**
 * One search result. No destination photo: the ticket frame runs in its plain mode on every width
 * (compact card on phones, full-width row on desktop), so nothing is requested from the image resolver.
 */
@Component({
    selector: 'app-card-flight',
    templateUrl: './card-flight.component.html',
    styleUrls: ['./card-flight.component.scss'],
    standalone: false
})
export class CardFlightComponent implements OnInit {

  private readonly airportCatalog = inject(AirportCatalogService);
  private readonly details = inject(FlightDetailsPanelService);

  @Input() flight: OriginDestinationOption;
  @Input() position: number;
  @Input() isMultiple: boolean;

  private summarizedSegments?: readonly FlightSegmentSch[];
  private summary: ItinerarySummary = summarizeCardItinerary([]);
  private header: HeaderSchedule = headerSchedule([]);

  /** City names come with the search result; the airport catalog only fills the gaps. */
  readonly cityOf: CityLookup = iata => {
    for (const segment of this.flight?.flightSegments ?? []) {
      if (segment.originLocationCode === iata && segment.originCityName) return segment.originCityName;
      if (segment.destinationLocationCode === iata && segment.destinationCityName) return segment.destinationCityName;
    }
    return this.airportCatalog.cityName(iata);
  };

  ngOnInit(): void {
    this.airportCatalog.load();
  }

  /** Recomputed only when the segment list changes, so the frame receives a stable `stops` array. */
  get itinerary(): ItinerarySummary {
    const segments = this.flight?.flightSegments ?? [];
    if (segments !== this.summarizedSegments) {
      this.summarizedSegments = segments;
      const legs = toLegs(segments);
      this.summary = summarizeCardItinerary(legs);
      this.header = headerSchedule(legs);
    }
    return this.summary;
  }

  get schedule(): HeaderSchedule {
    return this.itinerary && this.header;
  }

  get flightNumbers(): string {
    return (this.flight?.flightSegments ?? []).map(segment => `LA${segment.flightNumber}`).join(' / ');
  }

  /**
   * Flight time of a direct flight, as sent by the API. A connection has no total here:
   * its legs may land in different time zones, so adding local clocks would be a guess.
   */
  get durationLabel(): string {
    const segments = this.flight?.flightSegments ?? [];
    return segments.length === 1 ? formatDuration(segments[0].duration) : '';
  }

  /** Under the plane: "Voo direto · 1h 25min" when the API sent a duration; otherwise the frame's default. */
  get trackLabel(): string | null {
    const duration = this.durationLabel;
    return duration ? `Voo direto · ${duration}` : null;
  }

  /** Opens the vertical itinerary: a bottom sheet on phones, a right drawer elsewhere. */
  openDetails(): void {
    if (this.flight) this.details.show(this.flight);
  }
}
